import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAppSelector } from 'app';
import MessagingAPI from 'components/messaging/api/messaging-api';
import { ApplicationPropertyNames } from 'common/utils/shared/shared-constants';

interface SocialTestDealerContextType {
  isSocialTestDealer: boolean;
  isFacebookTestDealer: boolean;
  isInstagramTestDealer: boolean;
  isCheckingSocialTestDealer: boolean;
  refreshSocialTestDealerStatus: () => Promise<void>;
}

const SocialTestDealerContext = createContext<SocialTestDealerContextType | undefined>(undefined);

export const SocialTestDealerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isFacebookTestDealer, setIsFacebookTestDealer] = useState<boolean>(false);
  const [isInstagramTestDealer, setIsInstagramTestDealer] = useState<boolean>(false);
  const [isCheckingSocialTestDealer, setIsCheckingSocialTestDealer] = useState<boolean>(true);
  const dmsDealerId = useAppSelector((state) => state.user.dmsDealerId);

  // Derived value for backward compatibility
  const isSocialTestDealer = isFacebookTestDealer || isInstagramTestDealer;

  const checkTestDealer = useCallback(async (propertyName: string): Promise<boolean> => {
    try {
      const response = await MessagingAPI.getApplicationProperty(propertyName);
      if (response?.propertyValue) {
        const testDealerNumbers = response.propertyValue.split(',').map((d: string) => d.trim());
        return testDealerNumbers.includes('*') || testDealerNumbers.includes(dmsDealerId?.toString());
      }
      return false;
    } catch (error) {
      console.error(`Error checking ${propertyName}:`, error);
      return false;
    }
  }, [dmsDealerId]);

  const checkSocialTestDealer = useCallback(async () => {
    if (!dmsDealerId) {
      setIsFacebookTestDealer(false);
      setIsInstagramTestDealer(false);
      setIsCheckingSocialTestDealer(false);
      return;
    }

    try {
      setIsCheckingSocialTestDealer(true);
      const [facebookResult, instagramResult] = await Promise.all([
        checkTestDealer(ApplicationPropertyNames.FacebookTestDealers),
        checkTestDealer(ApplicationPropertyNames.InstagramTestDealers),
      ]);
      setIsFacebookTestDealer(facebookResult);
      setIsInstagramTestDealer(instagramResult);
    } catch (error) {
      console.error('Error checking social test dealer:', error);
      setIsFacebookTestDealer(false);
      setIsInstagramTestDealer(false);
    } finally {
      setIsCheckingSocialTestDealer(false);
    }
  }, [dmsDealerId, checkTestDealer]);

  useEffect(() => {
    checkSocialTestDealer();
  }, [checkSocialTestDealer]);

  const refreshSocialTestDealerStatus = useCallback(async () => {
    await checkSocialTestDealer();
  }, [checkSocialTestDealer]);

  return (
    <SocialTestDealerContext.Provider
      value={{
        isSocialTestDealer,
        isFacebookTestDealer,
        isInstagramTestDealer,
        isCheckingSocialTestDealer,
        refreshSocialTestDealerStatus,
      }}>
      {children}
    </SocialTestDealerContext.Provider>
  );
};

export const useSocialTestDealer = (): SocialTestDealerContextType => {
  const context = useContext(SocialTestDealerContext);
  if (!context) {
    throw new Error('useSocialTestDealer must be used within a SocialTestDealerProvider');
  }
  return context;
};

export default SocialTestDealerContext;
